import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, Loader2, LogIn, RefreshCw } from 'lucide-react'
import api, { handleApiError } from '../services/api'

type Status = 'verifying' | 'success' | 'error'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const email = searchParams.get('email')
  const [status, setStatus] = useState<Status>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const hasVerified = useRef(false)

  useEffect(() => {
    if (hasVerified.current) return
    hasVerified.current = true

    if (!token) {
      setStatus('error')
      setErrorMessage('קישור האימות לא תקין — לא נמצא אסימון אימות.')
      return
    }

    api
      .post('/auth/verify-email', { token })
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error')
        setErrorMessage(handleApiError(err))
      })
  }, [token])

  const handleResend = async () => {
    if (!email || resending) return
    setResending(true)
    try {
      await api.post('/auth/resend-verification', { email })
      setResent(true)
    } catch (err) {
      setErrorMessage(handleApiError(err))
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-bl from-primary-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-heading font-bold text-primary-600 mb-2">
            יפתח ונגר יועץ השקעות
          </h1>
          <p className="text-gray-600">אימות כתובת דוא"ל</p>
        </div>

        <div className="card text-center">
          {status === 'verifying' && (
            <div role="status">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                <Loader2 size={32} className="text-primary-600 animate-spin" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                מאמת את כתובת הדוא"ל...
              </h2>
              <p className="text-gray-600">אנא המתן/י רגע.</p>
            </div>
          )}

          {status === 'success' && (
            <div role="status">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                החשבון שלך אומת בהצלחה!
              </h2>
              <p className="text-gray-600 mb-2">
                כתובת הדוא"ל אושרה והחשבון שלך פעיל במערכת.
              </p>
              <p className="text-gray-600 mb-6">
                ניתן להתחבר כעת ולהתחיל בתהליך הייעוץ.
              </p>
              <Link to="/login" className="btn btn-primary inline-flex items-center gap-2">
                <LogIn size={18} aria-hidden="true" />
                <span>מעבר להתחברות</span>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div role="alert">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle size={32} className="text-red-600" aria-hidden="true" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                אימות נכשל
              </h2>
              <p className="text-gray-600 mb-4">{errorMessage}</p>

              {/* Resend verification */}
              {email && !resent && (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="btn btn-primary w-full mb-4 inline-flex items-center justify-center gap-2"
                >
                  {resending ? (
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw size={18} aria-hidden="true" />
                  )}
                  <span>שלח קישור אימות חדש</span>
                </button>
              )}

              {resent && (
                <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-800 justify-center" role="status">
                  <CheckCircle size={16} aria-hidden="true" />
                  <span>קישור אימות חדש נשלח לדוא"ל שלך!</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/register" className="btn btn-outline">
                  חזרה להרשמה
                </Link>
                <Link to="/login" className="btn btn-primary">
                  התחברות
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
